import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Radio,
  Select,
  Button,
  DatePicker,
  Input,
  message,
  Alert,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  List,
  Avatar,
  Divider,
  Breadcrumb
} from 'antd';
import {
  SendOutlined,
  TeamOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  WechatOutlined,
  EyeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import EmployeeSelect from '../components/common/EmployeeSelect';
import AttachmentManager from '../components/sop/AttachmentManager';
import MessagePreview from '../components/sop/MessagePreview';
import { useFixedFooter, useFixedFooterPlaceholder } from '../hooks/useFixedFooter';
import groupService from '../services/groupService';
import weworkService from '../services/weworkService';
import massSendService from '../services/massSendService';
import api from '../services/api';
import useMediaState from '../hooks/useMediaState';
import GroupSelect from '../components/common/GroupSelect';

const { Title, Text } = Typography;
const { Option } = Select;

const GroupMassSend = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsByOwner, setGroupsByOwner] = useState({});

  // 员工映射状态 - 用于群主姓名查找
  const [employeeMap, setEmployeeMap] = useState({});

  // 监听表单变化用于实时预览
  const [formValues, setFormValues] = useState({});

  // 直接管理attachments状态，模仿StepModal的做法
  const [attachments, setAttachments] = useState([]);

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 判断页面模式：view, copy, edit, create
  const getPageMode = () => {
    const path = location.pathname;
    if (path.includes('/view/')) return 'view';
    if (path.includes('/edit/')) return 'edit';
    if (location.state?.copyFrom) return 'copy';
    return 'create';
  };

  const pageMode = getPageMode();
  const isViewMode = pageMode === 'view';
  const isCopyMode = pageMode === 'copy';
  const isEditMode = pageMode === 'edit';

  // 使用媒体状态管理Hook
  const {
    mediaState,
    setMediaState,
  } = useMediaState();

  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // 加载群组和员工数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 同时加载群组数据和员工数据
        const [groupResponse, employeeResponse] = await Promise.all([
          groupService.getGroups(),
          weworkService.getAllEmployees()
        ]);

        // 处理分页数据格式
        let groupList = [];
        if (groupResponse && Array.isArray(groupResponse.content)) {
          groupList = groupResponse.content;
        } else if (Array.isArray(groupResponse)) {
          groupList = groupResponse;
        } else {
          console.warn('群组数据格式异常:', groupResponse);
          groupList = [];
        }

        console.log('处理后的群组列表:', groupList);
        console.log('群组样本数据:', groupList[0]); // 调试：查看群组数据结构
        setGroups(groupList);

        // 处理员工数据并存储到 window 对象中供 getOwnerName 使用
        const employeeList = employeeResponse?.filter(emp => emp.systemStatus === 1) || [];
        console.log('员工列表:', employeeList); // 调试：查看员工数据
        console.log('员工样本数据:', employeeList[0]); // 调试：查看员工数据结构

        // 本地员工映射 - 重点关注系统ID映射
        const localEmployeeMap = {};

        employeeList.forEach(emp => {
          // 主要映射：使用系统ID (emp.id) - 这是最重要的，对应后端 ownerId
          if (emp.id !== undefined && emp.id !== null) {
            localEmployeeMap[emp.id] = emp.name;
            localEmployeeMap[String(emp.id)] = emp.name; // 同时建立字符串版本的映射
          }

          // 备用映射：如果有其他ID字段也建立映射
          if (emp.userId) {
            localEmployeeMap[emp.userId] = emp.name;
            localEmployeeMap[String(emp.userId)] = emp.name;
          }

          if (emp.weworkUserId) {
            localEmployeeMap[emp.weworkUserId] = emp.name;
          }
        });

        // 兼容性处理：同时更新window对象
        window.employeeMap = { ...localEmployeeMap };

        // 设置本地员工映射
        setEmployeeMap(localEmployeeMap);

        console.log('本地员工映射表（前10项）:', Object.entries(localEmployeeMap).slice(0, 10)); // 调试
        console.log('员工ID类型示例:', employeeList.slice(0, 3).map(emp => ({
          id: emp.id,
          idType: typeof emp.id,
          userId: emp.userId,
          userIdType: typeof emp.userId,
          name: emp.name
        }))); // 调试

        // 按群主分组
        const groupsByOwnerMap = {};
        groupList.forEach(group => {
          const ownerId = group.ownerId;
          if (ownerId) {
            if (!groupsByOwnerMap[ownerId]) {
              groupsByOwnerMap[ownerId] = [];
            }
            groupsByOwnerMap[ownerId].push(group);
          }
        });

        console.log('按群主分组结果:', groupsByOwnerMap);
        console.log('群主ID列表:', Object.keys(groupsByOwnerMap)); // 调试：查看群主ID
        setGroupsByOwner(groupsByOwnerMap);

      } catch (error) {
        console.error('加载数据失败:', error);
        message.error('加载数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 加载任务数据（查看和编辑模式）
  useEffect(() => {
    const loadTaskData = async () => {
      if (taskId && (isViewMode || isEditMode)) {
        try {
          setLoading(true);
          const response = await massSendService.getTaskById(taskId);
          if (response.success && response.data) {
            const taskData = response.data;
            fillFormWithTaskData(taskData);
          } else {
            message.error('任务不存在');
            navigate('/group-mass-send');
          }
        } catch (error) {
          console.error('加载任务数据失败:', error);
          message.error('加载任务数据失败');
          navigate('/group-mass-send');
        } finally {
          setLoading(false);
        }
      }
    };

    loadTaskData();
  }, [taskId, isViewMode, isEditMode, navigate]);


  // 加载任务数据（查看和编辑模式）
  useEffect(() => {
    const loadTaskData = async () => {
      if (taskId && (isViewMode || isEditMode)) {
        try {
          setLoading(true);
          const response = await massSendService.getTaskById(taskId);
          if (response.success && response.data) {
            const taskData = response.data;
            fillFormWithTaskData(taskData);
          } else {
            message.error('任务不存在');
            navigate('/group-mass-send');
          }
        } catch (error) {
          console.error('加载任务数据失败:', error);
          message.error('加载任务数据失败');
          navigate('/group-mass-send');
        } finally {
          setLoading(false);
        }
      }
    };

    loadTaskData();
  }, [taskId, isViewMode, isEditMode, navigate]);

  // 处理复制模式的数据填充
  useEffect(() => {
    if (isCopyMode && location.state?.copyFrom && groups.length > 0) {
      const taskData = location.state.copyFrom;
      console.log('🔄 群组复制模式 - 接收到的任务数据:', taskData);
      console.log('👥 群组复制模式 - 当前可用群组:', groups);
      fillFormWithTaskData(taskData);
    }
  }, [isCopyMode, location.state, groups]);

  // 处理 AI 生成内容自动填入
  useEffect(() => {
    if (location.state?.aiGeneratedText && !isCopyMode && !isViewMode && !isEditMode) {
      const text = location.state.aiGeneratedText;
      const images = location.state.aiGeneratedImages || [];
      const newAttachments = [{ id: `text-${Date.now()}`, type: 'TEXT', content: text }];

      if (images.length > 0) {
        const ossUrls = {};
        images.forEach((img, i) => {
          ossUrls[i] = img.ossUrl || img.url;
        });
        newAttachments.push({
          id: `img-${Date.now()}`,
          type: 'IMAGE_GROUP',
          imageGroup: { mediaIds: [], ossUrls },
        });
      }

      setAttachments(newAttachments);
      message.info('已从 AI 生成内容填入');
    }
  }, [location.state]);

  // 填充表单数据
  const fillFormWithTaskData = (taskData) => {
    try {
      console.log('📋 群组开始填充表单数据:', taskData);
      // 解析附件数据
      let parsedAttachments = [];
      if (taskData.attachments) {
        try {
          const attachmentData = typeof taskData.attachments === 'string'
            ? JSON.parse(taskData.attachments)
            : taskData.attachments;

          if (Array.isArray(attachmentData)) {
            parsedAttachments = attachmentData;
          }
        } catch (e) {
          console.error('解析附件数据失败:', e);
        }
      }

      // 如果有文本内容但没有TEXT类型附件，添加一个
      if (taskData.textContent && !parsedAttachments.find(att => att.type === 'TEXT')) {
        parsedAttachments.unshift({
          id: `text-${Date.now()}`,
          type: 'TEXT',
          content: taskData.textContent
        });
      }

      // 如果没有任何附件，添加默认的TEXT附件
      if (parsedAttachments.length === 0) {
        parsedAttachments = [{
          id: `text-${Date.now()}`,
          type: 'TEXT',
          content: taskData.textContent || ''
        }];
      }

      setAttachments(parsedAttachments);

      // 解析目标配置
      let targetConfigObj = null;
      if (taskData.targetConfig) {
        try {
          targetConfigObj = typeof taskData.targetConfig === 'string'
            ? JSON.parse(taskData.targetConfig)
            : taskData.targetConfig;
        } catch (e) {
          console.error('解析目标配置失败:', e);
        }
      }

      // 填充表单 - 确保数据类型匹配
      const formData = {
        taskName: taskData.taskName,
        targetType: targetConfigObj?.targetType === 'GROUP_BY_OWNER' ? 'BY_OWNER' : 'SPECIFIC_GROUPS',
        // 确保群主ID转换为字符串，因为Option的key是字符串
        targetOwners: (targetConfigObj?.groupOwnerIds || []).map(id => String(id)),
        // 群组ID统一转为字符串，便于与下拉选项匹配
        targetGroups: (targetConfigObj?.groupIds || []).map(id => String(id)),
        scheduleType: taskData.sendTimeType || 'IMMEDIATE',
        scheduleDateTime: taskData.scheduledTime ? dayjs(taskData.scheduledTime) : null
      };

      console.log('📝 群组准备填充的表单数据:', formData);
      console.log('📎 群组解析后的附件数据:', parsedAttachments);
      console.log('🎯 群组目标配置对象:', targetConfigObj);
      console.log('👤 员工映射表:', employeeMap);
      console.log('👥 群主ID列表:', targetConfigObj?.groupOwnerIds);
      console.log('🔧 转换后的群主ID列表:', formData.targetOwners);
      console.log('🗝️ 可用的群主选项键:', Object.keys(groupsByOwner));
      console.log('🏢 群组ID列表(原始):', targetConfigObj?.groupIds);
      console.log('🏢 群组ID列表(表单):', formData.targetGroups);

      form.setFieldsValue(formData);
      setFormValues(formData);
    } catch (error) {
      console.error('填充表单数据失败:', error);
      message.error('填充表单数据失败');
    }
  };

  // 监听表单值变化
  const handleValuesChange = (changedValues, allValues) => {
    // 同步表单值到状态（除了attachments，因为attachments由单独状态管理）
    const { attachments: formAttachments, ...otherValues } = allValues;
    setFormValues({ ...otherValues, attachments });
  };

  // 初始化默认附件
  const initializeAttachments = () => {
    return []; // 返回空数组，让AttachmentManager内部的useEffect自动添加默认的TEXT附件
  };

  // 初始化attachments状态
  useEffect(() => {
    if (attachments.length === 0) {
      const defaultAttachments = [
        {
          id: `text-${Date.now()}`,
          type: 'TEXT',
          content: ''
        }
      ];
      setAttachments(defaultAttachments);
    }
  }, []);

  // 文件上传处理
  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError, onProgress, fieldKey, mediaType, stepType } = options;

    try {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

      // 获取租户ID
      const getTenantId = () => {
        try {
          const tenantStr = localStorage.getItem('tenant');
          if (tenantStr) {
            const tenant = JSON.parse(tenantStr);
            return tenant.id;
          }
        } catch (error) {
          console.error('获取租户ID失败:', error);
        }
        return null;
      };

      const tenantId = getTenantId();
      if (!tenantId) {
        throw new Error('无法获取租户信息');
      }

      // 根据文件类型确定mediaType
      let actualMediaType = 'image'; // 默认为图片
      if (file.type.startsWith('video/')) {
        actualMediaType = 'video';
      } else if (file.type.startsWith('image/')) {
        actualMediaType = 'image';
      } else {
        actualMediaType = 'file';
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', actualMediaType);
      formData.append('tenantId', tenantId);
      formData.append('sceneType', 'message'); // 群组群发使用message场景

      const response = await api.post('/media/wework/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
          if (onProgress) onProgress({ percent: progress });
        }
      });

      if (response.data.success) {
        const { mediaId, ossUrl } = response.data;

        // 更新预览URL
        setPreviewUrls(prev => ({ ...prev, [mediaId]: ossUrl }));

        // 调用成功回调
        if (onSuccess) {
          onSuccess({ mediaId, ossUrl, success: true });
        }
      } else {
        throw new Error(response.data.message || '上传失败');
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      if (onError) onError(error);
      message.error('文件上传失败');
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    }
  };

  // 文件删除处理
  const handleRemoveFile = (fieldKey, stepType, mediaIdToRemove = null) => {
    // 处理图片组的特殊删除逻辑
    if (mediaIdToRemove) {
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[mediaIdToRemove];
        return newUrls;
      });
    } else {
      // 普通文件删除
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[fieldKey];
        return newUrls;
      });
    }
  };

  // 获取表单初始值
  const getInitialValues = () => {
    return {
      targetType: 'BY_OWNER',
      scheduleType: 'IMMEDIATE',
      attachments: []
    };
  };

  // 提取文本内容
  const extractTextContent = (attachments) => {
    const textAttachment = attachments.find(att => att.type === 'TEXT');
    return textAttachment ? textAttachment.content || '' : '';
  };

  // 提取非文本附件
  const extractNonTextAttachments = (attachments) => {
    return attachments.filter(att => att.type !== 'TEXT');
  };

  // 提交任务
  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      // 提取文本内容和非文本附件
      const textContent = extractTextContent(attachments);
      const nonTextAttachments = extractNonTextAttachments(attachments);

      // 手动验证内容
      if (!textContent && nonTextAttachments.length === 0) {
        message.error('请添加发送内容');
        setSubmitLoading(false);
        return;
      }

      const values = await form.validateFields();

      // 确定目标类型和发送成员
      let targetType;
      let targetConfig = {};
      let taskMembers = [];

      if (values.targetType === 'BY_OWNER') {
        targetType = 'GROUP_BY_OWNER';
        const ownerIds = values.targetOwners || [];
        targetConfig = {
          targetType: 'GROUP_BY_OWNER',
          groupOwnerIds: ownerIds
        };
        taskMembers = ownerIds.map(id => ({ userId: id }));
      } else if (values.targetType === 'SPECIFIC_GROUPS') {
        targetType = 'GROUP';
        // 选择器使用字符串值，这里转换为数字id提交
        const groupIds = (values.targetGroups || []).map(v => typeof v === 'string' ? parseInt(v, 10) : v);
        targetConfig = {
          targetType: 'GROUP',
          groupIds: groupIds
        };
        // 从选中的群聊中推断出群主作为发送者
        const ownerIds = [...new Set(
          groups
            .filter(g => groupIds.includes(g.id))
            .map(g => g.ownerId)
            .filter(id => id) // 确保ownerId有效
        )];
        taskMembers = ownerIds.map(id => ({ userId: id }));
      } else {
        throw new Error('无效的目标类型');
      }

      if (taskMembers.length === 0) {
        message.error('无法确定发送成员，请检查群主或群聊选择');
        setSubmitLoading(false);
        return;
      }

      // 构建任务数据
      const taskData = {
        taskName: values.taskName,
        taskType: 'GROUP',
        targetType: targetType,
        textContent: textContent,
        attachments: nonTextAttachments.length > 0 ? JSON.stringify(nonTextAttachments) : null,
        sendTimeType: values.scheduleType,
        scheduledTime: values.scheduleType === 'SCHEDULED' ? values.scheduleDateTime?.format('YYYY-MM-DD HH:mm:ss') : null,
        targetConfig: JSON.stringify(targetConfig),
        members: taskMembers
      };

      console.log('提交的任务数据:', taskData);

      // 提交任务
      const result = await massSendService.createTask(taskData);

      if (values.scheduleType === 'IMMEDIATE') {
        // 立即执行
        await massSendService.executeTask(result.data.id);
        message.success('社群群发任务已提交并开始执行');
      } else {
        message.success('社群群发任务已创建，将在指定时间执行');
      }

      // 跳转回列表页
      navigate('/group-mass-send');

    } catch (error) {
      console.error('提交任务失败:', error);
      message.error(error.response?.data?.message || '提交任务失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    setFormValues({});

    // 重置attachments状态
    const defaultAttachments = [
      {
        id: `text-${Date.now()}`,
        type: 'TEXT',
        content: ''
      }
    ];
    setAttachments(defaultAttachments);
  };

  // 获取群主姓名
  const getOwnerName = (ownerId) => {
    console.log('🔍 查找群主姓名, ownerId:', ownerId, 'type:', typeof ownerId); // 调试

    // 确保ownerId是字符串格式以便匹配
    const ownerIdStr = String(ownerId);

    // 首先从本地员工映射中查找 (优先级最高)
    if (employeeMap[ownerIdStr]) {
      console.log('✅ 通过本地映射找到:', employeeMap[ownerIdStr]);
      return employeeMap[ownerIdStr];
    }

    // 尝试数字ID匹配 (因为后端ownerId是Long类型，对应员工的系统ID)
    if (employeeMap[ownerId]) {
      console.log('✅ 通过数字ID找到:', employeeMap[ownerId]);
      return employeeMap[ownerId];
    }

    // 从 window 对象的映射中查找 (兼容性处理)
    if (window.employeeMap && window.employeeMap[ownerIdStr]) {
      console.log('✅ 通过window映射找到:', window.employeeMap[ownerIdStr]);
      return window.employeeMap[ownerIdStr];
    }

    if (window.employeeMap && window.employeeMap[ownerId]) {
      console.log('✅ 通过window数字映射找到:', window.employeeMap[ownerId]);
      return window.employeeMap[ownerId];
    }

    // 从群组数据中查找群主信息 (后备方案)
    const group = groups.find(g => g.ownerId === ownerId);
    if (group && group.ownerName) {
      console.log('✅ 从群组数据找到 ownerName:', group.ownerName);
      return group.ownerName;
    }

    // 最后返回默认显示
    console.log('❌ 未找到群主姓名, ownerId:', ownerId);
    console.log('🔍 当前本地员工映射表（前5项）:', Object.keys(employeeMap).slice(0, 5));
    console.log('🔍 员工映射表中的数据类型示例:', Object.keys(employeeMap).map(k => `${k}(${typeof k})`).slice(0, 3));
    return `未知群主 (${ownerId})`;
  };

  // 获取选中的群聊列表
  const getSelectedGroups = () => {
    const { targetType, targetOwners, targetGroups } = formValues;
    let selectedGroups = [];

    if (targetType === 'BY_OWNER' && targetOwners) {
      targetOwners.forEach(ownerId => {
        if (groupsByOwner[ownerId]) {
          selectedGroups.push(...groupsByOwner[ownerId]);
        }
      });
    } else if (targetType === 'SPECIFIC_GROUPS' && targetGroups) {
      selectedGroups = groups.filter(group => (targetGroups || []).includes(String(group.id)));
    }

    return selectedGroups;
  };

  // 渲染选中群组列表
  const renderSelectedGroups = () => {
    const selectedGroups = getSelectedGroups();
    const { targetType } = formValues;

    if (!targetType || selectedGroups.length === 0) {
      return (
        <Alert
          message="未选择发送对象"
          description="请先选择群主或指定群聊"
          type="warning"
          showIcon
          style={{ marginBottom: '12px', padding: '10px 24px', border: 'none' }}
        />
      );
    }

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 500,
          color: 'rgba(0, 0, 0, 0.85)'
        }}>
          <TeamOutlined style={{ marginRight: 4 }} />
          将发送到以下 {selectedGroups.length} 个群聊：
        </div>
        <div style={{
          maxHeight: '200px',
          overflowY: 'auto',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          padding: '12px',
          backgroundColor: '#fafafa'
        }}>
          <List
            size="small"
            dataSource={selectedGroups}
            renderItem={(group) => (
              <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<WechatOutlined />}
                      style={{ backgroundColor: '#52c41a' }}
                      size="small"
                    />
                  }
                  title={
                    <div style={{ fontSize: '13px' }}>
                      <Text strong>{group.name}</Text>
                      {group.memberCount && (
                        <Tag size="small" style={{ marginLeft: '8px' }}>
                          {group.memberCount}人
                        </Tag>
                      )}
                    </div>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      群主：{getOwnerName(group.ownerId)}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      </div>
    );
  };

  // 获取页面标题
  const getPageTitle = () => {
    switch (pageMode) {
      case 'view': return '查看社群群发';
      case 'copy': return '复制社群群发';
      default: return '创建社群群发';
    }
  };

  // 返回列表页
  const handleGoBack = () => {
    navigate('/group-mass-send');
  };

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item onClick={() => navigate('/group-mass-send')}>
          <Title level={5} style={{ cursor: 'pointer', color: '#1677ff' }}>社群群发</Title>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Title level={5}>{getPageTitle()}</Title>
        </Breadcrumb.Item>
      </Breadcrumb>
      {isViewMode && (
        <Tag icon={<EyeOutlined />} color="blue">
          只读模式
        </Tag>
      )}

      <Row gutter={24}>
        {/* 左侧配置区域 */}
        <Col span={14}>
          <Card title="群发设置" loading={loading}>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleValuesChange}
              initialValues={getInitialValues()}
            >
              {/* 群发名称 */}
              <Form.Item
                name="taskName"
                label="群发名称"
                rules={[{ required: true, message: '请输入群发名称' }]}
              >
                <Input
                  placeholder="请输入群发任务名称"
                  disabled={isViewMode}
                />
              </Form.Item>

              {/* 发送对象 */}
              <Form.Item
                name="targetType"
                label={<>发送对象</>}
                rules={[{ required: true, message: '请选择发送对象' }]}
              >
                <Radio.Group disabled={isViewMode}>
                  <Radio value="BY_OWNER">按群主选择</Radio>
                  <Radio value="SPECIFIC_GROUPS">指定群聊</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 群主选择 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.targetType !== currentValues.targetType
                }
              >
                {({ getFieldValue }) => {
                  const targetType = getFieldValue('targetType');

                  if (targetType === 'BY_OWNER') {
                    return (
                      <Form.Item
                        name="targetOwners"
                        label="选择群主"
                        rules={[{ required: true, message: '请至少选择一个群主' }]}
                        help="选择群主后，将向该群主的所有群聊发送消息"
                      >
                        <EmployeeSelect
                          multiple
                          placeholder="请选择群主（可多选，支持按分组/通讯录选择）"
                          disabled={isViewMode}
                          width="100%"
                        />
                      </Form.Item>
                    );
                  } else if (targetType === 'SPECIFIC_GROUPS') {
                    return (
                      <Form.Item
                        name="targetGroups"
                        label="选择群聊"
                        rules={[{ required: true, message: '请至少选择一个群聊' }]}
                      >
                        <GroupSelect
                          groups={groups}
                          mode="multiple"
                          placeholder="请选择群聊（可多选）"
                          disabled={isViewMode}
                          valueKey="id"
                          labelKey="name"
                          valueType="string"
                          showOwner
                          renderOptionLabel={(group) => (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 500 }}>{group.name}</span>
                              {/* <Text type="secondary" style={{ fontSize: '12px' }}>
                                群主: {getOwnerName(group.ownerId)}
                              </Text> */}
                            </div>
                          )}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>

              {/* 选中群组预览 */}
              {renderSelectedGroups()}

              <Divider />

              {/* 发送内容 */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
                    发送内容
                  </label>
                </div>
                <AttachmentManager
                  stepType="MESSAGE"
                  attachments={attachments}
                  mediaState={mediaState}
                  previewUrls={previewUrls}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  readOnly={isViewMode}
                  handleFileUpload={handleFileUpload}
                  handleRemoveFile={handleRemoveFile}
                  onAddAttachment={(type) => {
                    const newAttachment = {
                      id: `${type.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                      type: type
                    };

                    // 根据类型初始化不同的字段
                    switch (type) {
                      case 'TEXT':
                        newAttachment.content = '';
                        break;
                      case 'IMAGE_GROUP':
                        newAttachment.imageGroup = { mediaIds: [], ossUrls: {} };
                        break;
                      case 'VIDEO':
                        newAttachment.mediaId = '';
                        newAttachment.ossUrl = '';
                        newAttachment.videoTitle = '';
                        newAttachment.videoDescription = '';
                        break;
                      case 'LINK':
                        newAttachment.title = '';
                        newAttachment.description = '';
                        newAttachment.url = '';
                        newAttachment.picUrl = '';
                        break;
                      case 'MINIPROGRAM':
                        newAttachment.title = '';
                        newAttachment.appid = '';
                        newAttachment.pagepath = '';
                        newAttachment.thumbMediaId = '';
                        newAttachment.thumbMediaId_ossUrl = '';
                        break;
                      default:
                        newAttachment.content = '';
                    }

                    setAttachments(prevAttachments => [...prevAttachments, newAttachment]);

                    // 返回新附件的ID，供素材选择逻辑使用
                    return newAttachment.id;
                  }}
                  onRemoveAttachment={(attachmentId) => {
                    setAttachments(prevAttachments =>
                      prevAttachments.filter(att => att.id !== attachmentId)
                    );
                  }}
                  onUpdateContent={(attachmentId, field, value) => {
                    console.log('🔄 GroupMassSend - onUpdateContent 被调用:', {
                      attachmentId,
                      field,
                      value,
                      valueType: typeof value
                    });

                    // 使用函数式更新避免状态竞争
                    setAttachments(prevAttachments => {
                      const newAttachments = prevAttachments.map(att =>
                        att.id === attachmentId ? { ...att, [field]: value } : att
                      );

                      console.log('📋 更新后的attachments:', newAttachments.find(att => att.id === attachmentId));

                      // 在状态更新完成后，更新mediaState
                      const currentAttachment = newAttachments.find(att => att.id === attachmentId);
                      const attachmentType = currentAttachment?.type;

                      // 同时更新mediaState以确保AttachmentManager正常工作
                      if (field === 'mediaId' || field === 'videoMediaId' || field === 'fileMediaId') {
                        setMediaState(prev => {
                          const newState = { ...prev };
                          newState.messageMediaIds = {
                            ...prev.messageMediaIds,
                            [attachmentId]: value
                          };

                          if (field === 'mediaId' && attachmentType === 'IMAGE') {
                            newState.messageImageIds = {
                              ...prev.messageImageIds,
                              [attachmentId]: value
                            };
                          } else if (field === 'videoMediaId' || (field === 'mediaId' && attachmentType === 'VIDEO')) {
                            newState.messageVideoIds = {
                              ...prev.messageVideoIds,
                              [attachmentId]: value
                            };
                          } else if (field === 'fileMediaId' || (field === 'mediaId' && attachmentType === 'FILE')) {
                            newState.messageFileIds = {
                              ...prev.messageFileIds,
                              [attachmentId]: value
                            };
                          }

                          return newState;
                        });
                      }

                      // 处理ossUrl字段 - 包括小程序缩略图的特殊处理
                      if (field === 'ossUrl' || field === 'videoMediaId_ossUrl' || field === 'mediaId_ossUrl' || field === 'thumbMediaId_ossUrl') {
                        console.log('🖼️ 处理OSS URL字段:', { field, value, attachmentType });

                        setMediaState(prev => {
                          return {
                            ...prev,
                            messageOssUrls: {
                              ...prev.messageOssUrls,
                              [attachmentId]: value
                            }
                          };
                        });

                        // 如果是小程序缩略图，同时更新previewUrls以便立即预览
                        if (field === 'thumbMediaId_ossUrl' && attachmentType === 'MINIPROGRAM' && value) {
                          const thumbMediaId = currentAttachment?.thumbMediaId;
                          if (thumbMediaId) {
                            console.log('🔄 更新小程序缩略图预览URL:', { thumbMediaId, value });
                            setPreviewUrls(prev => ({ ...prev, [thumbMediaId]: value }));
                          }
                        }
                      }

                      // 处理imageGroup字段
                      if (field === 'imageGroup' && attachmentType === 'IMAGE_GROUP') {
                        setMediaState(prev => {
                          return {
                            ...prev,
                            messageImageGroupIds: {
                              ...prev.messageImageGroupIds,
                              [attachmentId]: value
                            }
                          };
                        });
                      }

                      return newAttachments;
                    });
                  }}
                  onUpdateType={(attachmentId, newType) => {
                    setAttachments(prevAttachments => prevAttachments.map(att => {
                      if (att.id === attachmentId) {
                        // 重置附件，保留id和type，清空其他字段
                        const resetAttachment = { id: att.id, type: newType };

                        // 根据新类型初始化字段
                        switch (newType) {
                          case 'TEXT':
                            resetAttachment.content = '';
                            break;
                          case 'IMAGE_GROUP':
                            resetAttachment.imageGroup = { mediaIds: [], ossUrls: {} };
                            break;
                          case 'VIDEO':
                            resetAttachment.mediaId = '';
                            resetAttachment.ossUrl = '';
                            resetAttachment.videoTitle = '';
                            resetAttachment.videoDescription = '';
                            break;
                          case 'LINK':
                            resetAttachment.title = '';
                            resetAttachment.description = '';
                            resetAttachment.url = '';
                            resetAttachment.picUrl = '';
                            break;
                          case 'MINIPROGRAM':
                            resetAttachment.title = '';
                            resetAttachment.appid = '';
                            resetAttachment.pagepath = '';
                            resetAttachment.thumbMediaId = '';
                            resetAttachment.thumbMediaId_ossUrl = '';
                            break;
                          default:
                            resetAttachment.content = '';
                        }

                        return resetAttachment;
                      }
                      return att;
                    }));
                  }}
                  handleUpdatePreviewUrls={(newUrls) => {
                    setPreviewUrls(prev => ({ ...prev, ...newUrls }));
                  }}
                />
              </div>

              <Divider />

              {/* 群发时间 */}
              <Form.Item
                name="scheduleType"
                label={<>群发时间</>}
                rules={[{ required: true, message: '请选择群发时间' }]}
              >
                <Radio.Group disabled={isViewMode}>
                  <Radio value="IMMEDIATE">立即发送</Radio>
                  <Radio value="SCHEDULED">定时发送</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 定时发送时间 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.scheduleType !== currentValues.scheduleType
                }
              >
                {({ getFieldValue }) => {
                  const scheduleType = getFieldValue('scheduleType');

                  if (scheduleType === 'SCHEDULED') {
                    return (
                      <Form.Item
                        name="scheduleDateTime"
                        label="发送时间"
                        rules={[
                          { required: true, message: '请选择发送时间' },
                          {
                            validator: (_, value) => {
                              if (value && value.isBefore(dayjs())) {
                                return Promise.reject(new Error('发送时间不能早于当前时间'));
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime={{ format: 'HH:mm' }}
                          format="YYYY-MM-DD HH:mm"
                          placeholder="请选择发送时间"
                          style={{ width: '100%' }}
                          disabled={isViewMode}
                          disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* 右侧预览区域 */}
        <Col span={10}>
          <MessagePreview
            step={{
              type: 'MESSAGE',
              actionType: 'SEND_MESSAGE',
              messageList: attachments.map(attachment => {
                if (attachment.type === 'TEXT') {
                  return {
                    type: 'TEXT',
                    params: { content: attachment.content || '' }
                  };
                } else if (attachment.type === 'IMAGE_GROUP') {
                  return {
                    type: 'IMAGE_GROUP',
                    params: {
                      imageGroup: attachment.imageGroup || { mediaIds: [], ossUrls: {} }
                    }
                  };
                } else if (attachment.type === 'VIDEO') {
                  return {
                    type: 'VIDEO',
                    params: {
                      mediaId: attachment.mediaId || '',
                      ossUrl: attachment.ossUrl || ''
                    }
                  };
                } else if (attachment.type === 'LINK') {
                  return {
                    type: 'LINK',
                    params: {
                      title: attachment.title || '',
                      url: attachment.url || '',
                      description: attachment.description || '',
                      picUrl: attachment.picUrl || '',
                      picUrl_ossUrl: attachment.picUrl_ossUrl || ''
                    }
                  };
                } else if (attachment.type === 'MINIPROGRAM') {
                  return {
                    type: 'MINIPROGRAM',
                    params: {
                      title: attachment.title || '',
                      appid: attachment.appid || '',
                      pagepath: attachment.pagepath || '',
                      thumbMediaId: attachment.thumbMediaId || '',
                      thumbMediaId_ossUrl: attachment.thumbMediaId_ossUrl || ''
                    }
                  };
                }
                return null;
              }).filter(Boolean)
            }}
            noTruncate={true}
          />
        </Col>
      </Row>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      {!isViewMode && (
        <div style={fixedFooterStyle}>
          <Space>
            <Button size="middle" onClick={() => navigate('/group-mass-send')}>
              取消
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitLoading}
              onClick={handleSubmit}
              size="middle"
            >
              {isCopyMode ? '创建副本' : '提交群发'}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default GroupMassSend; 
